import { ref, computed } from 'vue'
import { login as loginApi, logout as logoutApi, fetchMe } from '~/composables/api'
// Not re-exported by the `~/composables/api` barrel; imported from the module
// directly (same source the barrel pulls `login`/`logout` from, so no new graph).
import { getSessionId } from '~/composables/api/useAuthApi'
import { AuthErrorCode } from '#layers/base/utils/authErrors'
import { writeTopbarCacheFromUser, clearTopbarCache } from '~/utils/topbarCache'
import { consumeAuthRedirect } from '~/utils/authRedirect'
import { resetVoucherActivationState } from '~/composables/useVoucherActivation'

// OAuth providers narrowed to Google only (Apple dropped in Supabase Auth migration).
// Local (not exported) — the canonical auto-imported `OAuthProvider` is the
// identical type in useSupabaseAuth.ts; exporting both collides in Nuxt auto-imports.
type OAuthProvider = 'google'

function mapPrecheckReason(reason: string | undefined, message: string | undefined, t: (key: string) => string): string {
  switch (reason) {
    case 'user_banned':
      // Server provides ban_reason via `message`; fall back to generic copy.
      return message || t('errors.auth.accountBanned')
    case 'plus_alias_not_allowed':
      return t('errors.auth.plusAliasNotAllowed')
    case 'already_registered':
      return t('errors.auth.alreadyRegistered')
    case 'service_unavailable':
      return t('errors.auth.serviceUnavailable')
    case 'disposable':
      return t('errors.auth.disposableEmail')
    case 'invalid_format':
      return t('errors.auth.invalidEmail')
    default:
      return t('errors.auth.invalidEmailDefault')
  }
}

interface AuthState {
  isLoading: boolean
  error: string | null
}

/**
 * Main authentication composable — façade over `useSupabaseAuth` + backend.
 *
 * Supports multiple authentication methods:
 * - Email: Passwordless OTP code
 * - OAuth: Google (Apple removed)
 * - EVM wallets via SIWE (MetaMask, Coinbase Wallet)
 * - Solana wallets via SIWS (Phantom, Solflare, Backpack, Ledger)
 *
 * Flow:
 * 1. User authenticates via chosen method (email/OAuth/wallet)
 * 2. Supabase Auth validates and returns access token
 * 3. Backend receives token and creates/retrieves user session
 * 4. Stores are updated with user data
 */
export function useAuth() {
  const { t } = useI18n()

  /**
   * Render a thrown auth error as user-facing text.
   *
   * `parseAuthError` hands back a translation key whenever the copy is ours,
   * and only a bare `message` when the string came from the server (a ban
   * reason, an edge-function validation message) — which is already the most
   * specific thing we can show, in whatever language the backend produced.
   */
  function authErrorText(error: unknown): string {
    const parsed = parseAuthError(error)
    return parsed.messageKey ? t(parsed.messageKey) : parsed.message
  }

  const {
    isAuthenticated: supabaseAuthenticated,
    walletAddress,
    solanaAddress,
    authChainType,
    authMethod: supabaseAuthMethod,
    // Wallet auth
    loginWithSiwe,
    loginWithSiws,
    // Email auth
    sendEmailCode: supabaseSendEmailCode,
    loginWithEmailCode: supabaseLoginWithEmailCode,
    // OAuth
    loginWithOAuth: supabaseLoginWithOAuth,
    completeOAuthLogin: supabaseCompleteOAuthLogin,
    // Account linking
    sendLinkEmailCode: supabaseSendLinkEmailCode,
    linkEmailWithCode: supabaseLinkEmailWithCode,
    linkEvmWallet: supabaseLinkEvmWallet,
    linkSolanaWallet: supabaseLinkSolanaWallet,
    linkOAuth: supabaseLinkOAuth,
    // Session
    logout: supabaseLogout,
    getAccessToken,
  } = useSupabaseAuth()

  const { precheckEmail } = useEmailPrecheck()

  const evmWallet = useEvmWallet()

  const userStore = useUserStore()
  const balanceStore = useBalanceStore()
  const characterStore = useCharacterStore()
  const oreStore = useOreStore()
  const bonusStore = useBonusStore()
  const freeBetsStore = useFreeBetsStore()
  const uiStore = useUIStore()
  const router = useRouter()
  const { getReferralCode, getAttribution, clearReferralCode, clearAttribution } = useReferral()
  const { subscribe: subscribeToBalance, unsubscribe: unsubscribeFromBalance } = useBalanceRealtime()
  const { subscribe: subscribeToDeposits, unsubscribe: unsubscribeFromDeposits } = useDepositRealtime()
  const { setUser: setSentryUser, clearUser: clearSentryUser } = useSentry()

  const state = ref<AuthState>({
    isLoading: false,
    error: null,
  })

  // ─── Email Authentication ───────────────────────────────────────────

  /**
   * Send OTP code to email (first step of email login)
   */
  async function sendEmailCode(email: string): Promise<boolean> {
    state.value.error = null
    useAnalytics().track('login_email_submitted')
    try {
      const precheck = await precheckEmail(email)
      if (!precheck.allowed) {
        state.value.error = mapPrecheckReason(precheck.reason, precheck.message, t)
        return false
      }
      return await supabaseSendEmailCode(email)
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    }
  }

  /**
   * Verify email OTP code and complete login (second step)
   */
  async function verifyEmailCode(email: string, code: string): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      await supabaseLoginWithEmailCode(email, code)
      return await completeBackendAuth()
    } catch (error: unknown) {
      console.error('[useAuth] verifyEmailCode failed:', error)
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  // ─── OAuth Authentication ─────────────────────────────────────────

  /**
   * Start OAuth login (redirects to provider)
   */
  async function loginWithOAuth(provider: OAuthProvider): Promise<void> {
    state.value.isLoading = true
    state.value.error = null
    if (provider === 'google') useAnalytics().track('login_google_clicked')

    try {
      await supabaseLoginWithOAuth(provider)
      // User will be redirected — no further action here
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      state.value.isLoading = false
    }
  }

  /**
   * Complete OAuth login after redirect callback.
   *
   * Signature preserved for callsite compatibility (auth/callback.vue passes
   * code/state/provider from URL params). The underlying Supabase PKCE
   * exchange happens via `detectSessionInUrl: true` in the plugin, so all
   * three params are forwarded but ignored — `completeOAuthLogin` here
   * waits for the resulting SIGNED_IN event then runs backend auth.
   */
  async function completeOAuthLogin(code: string, stateParam: string, urlProvider?: string): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      await supabaseCompleteOAuthLogin(code, stateParam, urlProvider)
      return await completeBackendAuth()
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  // ─── Wallet Authentication ────────────────────────────────────────

  /**
   * Login with EVM wallet (MetaMask, Coinbase Wallet)
   */
  async function loginWithEvm(
    walletType: 'metamask' | 'coinbase' = 'metamask'
  ): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      // Step 1: Connect EVM wallet
      const address = await evmWallet.connect(walletType)
      const chainId = evmWallet.getFormattedChainId()

      // Step 2: Authenticate with Supabase (SIWE). `walletType` is forwarded
      // for symmetry with the old Privy signature; useSupabaseAuth ignores it.
      await loginWithSiwe(address, chainId, evmWallet.signMessage, walletType)

      // Step 3: Complete backend authentication
      return await completeBackendAuth()
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  /**
   * Login with Solana wallet (Phantom, Solflare, etc.)
   */
  async function loginWithSolana(
    publicKeyBase58: string,
    signMessage: (message: Uint8Array) => Promise<Uint8Array>
  ): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      await loginWithSiws(publicKeyBase58, signMessage)
      return await completeBackendAuth()
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  // ─── Backend Auth ─────────────────────────────────────────────────

  /**
   * Complete authentication with backend after Supabase Auth succeeds
   */
  async function completeBackendAuth(): Promise<boolean> {
    // Get Supabase access token
    const accessToken = await getAccessToken()
    if (!accessToken) {
      throw new Error('Failed to get access token')
    }

    // Get visitor ID + a FRESH Pro requestId for server-side verification.
    // The Server API rejects identifications older than ~60s, so we MUST do
    // a fresh `fp.get()` call here — not reuse cached values from page-load.
    const visitorStore = useVisitorStore()
    const identity = useIdentity()
    let visitorId = identity.state.value.visitor_id ?? visitorStore.visitorId

    const { $fingerprint } = useNuxtApp()
    let fingerprintRequestId: string | null = null
    if ($fingerprint?.identify) {
      try {
        const fresh = await $fingerprint.identify()
        if (fresh) {
          // Pro v4 returned a fresh pair. Always prefer Pro's visitorId for
          // signups (when visitorId from localStorage is empty); for existing
          // visitor_id we still send the cached value so the server's
          // visitor_mismatch detection can catch tampering.
          if (!visitorId) visitorId = fresh.visitorId
          fingerprintRequestId = fresh.requestId
        } else if (!visitorId && $fingerprint.getVisitorId) {
          // Agent unavailable (no Pro key / blocked) — fall back to cached
          // visitor id, no requestId. Backend treats absent requestId as
          // "unverified" and trusts the client visitorId as a hint.
          visitorId = await $fingerprint.getVisitorId()
        }
      } catch (err) {
        console.warn('[useAuth] Fingerprint Pro call failed:', err)
      }
    } else if (!visitorId && $fingerprint?.getVisitorId) {
      // Old plugin (no identify) — best-effort fallback.
      try {
        visitorId = await $fingerprint.getVisitorId()
      } catch (err) {
        console.warn('[useAuth] Fingerprint Pro fallback failed:', err)
      }
    }

    // Authenticate with backend, passing auth method for free bet logic
    let response
    try {
      response = await loginApi({
        accessToken,
        referralCode: getReferralCode(),
        authMethod: supabaseAuthMethod.value,
        visitorId,
        fingerprintRequestId,
        attribution: getAttribution(),
      })
    } catch (error: unknown) {
      const parsed = parseAuthError(error)
      if (parsed.code === AuthErrorCode.ACCOUNT_BANNED) {
        // Lock the UI permanently for this browser. Backend already linked
        // the visitor + IP to the banned account (visitor upsert runs BEFORE
        // the ban check returns 403), so the abuser can't open a new account
        // on this fingerprint without tripping identity-risk.
        identity.markBanned()
        const { error: toastError } = useToast()
        toastError(
          t('errors.auth.accountBannedTitle'),
          parsed.messageKey ? t(parsed.messageKey) : parsed.message,
        )
        await supabaseLogout().catch(() => {})
      }
      throw error
    }

    if (!response?.user?.id) {
      console.error('[useAuth] completeBackendAuth: loginApi returned unexpected response:', JSON.stringify(response))
      throw new Error('Login failed: server returned invalid response')
    }

    // Identity state machine: this browser is now bound to a registered user.
    // Locks the free-bet UI permanently and clears the played-visitor flag
    // (visitor row is now associated with the user_id in the DB).
    identity.markRegistered(null)

    // Retire any outstanding email code. This lives here rather than at the
    // login-modal callsites because EVERY entry point funnels through this
    // method — including Google, which completes on /auth/callback with the
    // modal long since unmounted. Clearing per-callsite left that one path out
    // and would have gone stale again the next time a method was added: a
    // record surviving a login resurfaces on the next modal open (after a
    // logout, say) as a restored code step for an address nobody asked about.
    useOtpSession().clear()

    // Update stores
    userStore.setUser(response.user)
    // Optimistic topbar cache: seed it on first login so the very next reload
    // paints the authed shell without a flicker (spec C6). demo.vue sets the
    // store directly and is naturally excluded.
    writeTopbarCacheFromUser(response.user)
    setSentryUser(response.user.id)
    await balanceStore.fetchBalance(accessToken)
    // Non-blocking: cosmetics, ore, and free bets are visual-only, don't block auth flow
    characterStore.fetchInventory().catch(() => {})
    oreStore.fetchOreData(accessToken).catch(() => {})
    freeBetsStore.fetchFreeBets(accessToken).catch(() => {})

    // Deposit-wallet backfill: on first login for a new chain (Tron, historically
    // Solana too), createServerWallet is fire-and-forget in services/user.ts, so
    // auth-login's dbUser snapshot has deposit_wallet_*_address=null for chains
    // whose wallet creation hasn't completed yet. /auth-session backfills from
    // the DB once the wallet row lands, but our cached response above doesn't.
    //
    // Poll with exponential backoff up to 10s — wallet creation can exceed
    // 2s under load. Stop early as soon as all wallet addresses populate, OR
    // when the deadline is hit (next page load will backfill via auth.client.ts).
    const needsBackfill =
      !response.user.depositWalletAddress
      || !response.user.depositWalletSolanaAddress
      || !response.user.depositWalletTronAddress
    if (needsBackfill) {
      const pollBackfill = async () => {
        const deadline = Date.now() + 10_000
        const waits = [1000, 1500, 2000, 2500, 3000]
        for (const wait of waits) {
          if (Date.now() > deadline) return
          await new Promise((r) => setTimeout(r, wait))
          await userStore.fetchUser(accessToken).catch(() => {})
          // Stop early if ALL wallet addresses now populated
          if (
            userStore.depositWalletAddress
            && userStore.depositWalletSolanaAddress
            && userStore.depositWalletTronAddress
          ) return
        }
      }
      pollBackfill().catch(() => {
        // Non-critical — next page load will backfill via auth.client.ts plugin
      })
    }

    // Subscribe to real-time balance updates
    subscribeToBalance(response.user.id)
    subscribeToDeposits(response.user.id)

    // Reconnect realtime with authentication token
    const { reconnectWithAuth } = useSupabaseRealtime()
    await reconnectWithAuth()

    // Fires on EVERY completed auth (email/OAuth/wallet), shared success path.
    // isNewUser distinguishes a first-time registration from a returning login,
    // so the one event covers both funnels via its tag.
    if (supabaseAuthMethod.value) {
      useAnalytics().track('auth_completed', {
        authMethod: supabaseAuthMethod.value,
        isNewUser: response.user.isNewUser ? 'true' : 'false',
      })
    }

    // Clear referral code after successful attribution, and the consumed
    // attribution cookie with it — a later signup from the same browser must
    // not replay this journey's utm/click id (duplicate ad-network lead).
    // Unconditional (not gated on isNewUser): an existing user's login also
    // ends the journey, and a shared-browser signup after it must start clean.
    clearReferralCode()
    clearAttribution()

    // Always clear visitor state on login — play.vue will re-register
    // with fingerprint and get fresh eligibility from backend
    visitorStore.clearVisitor()

    // Fresh registrations land on /wallet with the deposit modal already open —
    // the shortest path from signup to a funded account. Returning users keep
    // going to the game. This covers every signup route (email OTP, Google
    // OAuth, EVM and Solana wallets) because all of them funnel through here.
    //
    // The modal is opened after the navigation resolves: LoginModal closes
    // itself on route change, and doing it in this order means that watcher
    // can't race ahead and close the deposit modal instead. `activeModal` is a
    // single slot, so opening deposit also supersedes the login modal.
    // Consume the stashed destination on both branches: a returning user is
    // sent back to where the auth middleware intercepted them (the notification
    // settings an Unsubscribe link points at, most often), while a fresh
    // registration keeps the deposit funnel — but still clears the value so a
    // stale path cannot hijack a later navigation.
    const redirect = consumeAuthRedirect()

    // Navigation must never invalidate a session that is already established:
    // every caller wraps completeBackendAuth in a try/catch that turns a throw
    // into "login failed", so a rejected push — a stale lazy chunk, or a throw
    // in the target page's setup — would tell a fully authenticated user their
    // login did not work. Landing on the wrong page is the lesser failure.
    const goTo = async (path: string): Promise<void> => {
      try {
        await router.push(path)
      } catch (err) {
        console.error('[useAuth] post-login navigation failed:', err)
        if (path !== '/') await router.push('/').catch(() => {})
      }
    }

    if (response.user.isNewUser) {
      await goTo('/wallet')
      uiStore.openDepositModal()
    } else {
      uiStore.closeLoginModal()
      await goTo(redirect ?? '/')
    }

    return true
  }

  // ─── Account Linking ──────────────────────────────────────────────

  /**
   * Send OTP code to link an email to the current account
   */
  async function sendLinkEmailCode(email: string): Promise<boolean> {
    state.value.error = null
    try {
      const precheck = await precheckEmail(email)
      if (!precheck.allowed) {
        state.value.error = mapPrecheckReason(precheck.reason, precheck.message, t)
        return false
      }
      return await supabaseSendLinkEmailCode(email)
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    }
  }

  /**
   * Verify code and link (or update) email on current account.
   *
   * Supabase's `verifyOtp({ type: 'email_change' })` works for both first-time
   * linking and updating an existing email — no need to branch on whether the
   * user already had an email linked. The backend notification POST to
   * `/user-link-method` (for free-bet + profile sync) is fired by
   * useSupabaseAuth.linkEmailWithCode itself.
   */
  async function linkEmailWithCode(email: string, code: string): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      await supabaseLinkEmailWithCode(email, code)
      // `userStore.user.email` is what drives `hasEmail` — and with it the
      // "add email to enable withdrawals" banner on /wallet, the BalanceCard
      // withdraw guard and the logout warning. It is only ever populated by
      // /auth-session, which nothing else re-hits after a link, so without
      // this refresh the banners survive until the next full app init
      // (issue #787).
      //
      // Isolated try/catch on purpose: the email is already linked by the time
      // we get here, so nothing this refresh does may turn a successful link
      // into a reported failure. fetchUser reports its own errors to Sentry.
      //
      // The snapshot/restore is NOT belt-and-braces. `fetchUser` swallows its
      // own errors and sets `user.value = null` on failure (stores/user.ts),
      // and `isAuthenticated` is `user.value !== null` — so a transient
      // /auth-session 5xx here would flip the entire app to logged-out while
      // this function still returned true. Nothing called fetchUser after a
      // link before, so that path is new; keep the previous user on failure.
      const previousUser = userStore.user
      try {
        const accessToken = await getAccessToken()
        if (accessToken) await userStore.fetchUser(accessToken)
      } catch (refreshError: unknown) {
        console.warn('[useAuth] post-link session refresh failed:', refreshError)
      }
      // Only heal a TRANSIENT failure. On a 401 the global $fetch interceptor
      // runs handle401Logout() synchronously before the rejection reaches
      // fetchUser — it removes aurumrun_session_id, sweeps the sb-* tokens and
      // clears the store. Restoring the user there would resurrect a session
      // that was deliberately torn down. The session id is the discriminator:
      // still present means the store was emptied by a 5xx, not a logout.
      if (!userStore.user && previousUser && getSessionId()) {
        userStore.setUser(previousUser)
      }
      return true
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  /**
   * Link an EVM wallet (MetaMask, Coinbase) to the current account.
   * Used by profile.vue (§6.9). Pulls the signer from useEvmWallet —
   * caller is expected to have invoked `evmWallet.connect()` first
   * (typically via the same wallet-selection UI as loginWithEvm).
   */
  async function linkEvmWallet(
    walletType: 'metamask' | 'coinbase' = 'metamask'
  ): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      const address = await evmWallet.connect(walletType)
      const chainId = evmWallet.getFormattedChainId()
      await supabaseLinkEvmWallet(address, chainId, evmWallet.signMessage)
      return true
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  /**
   * Link a Solana wallet (Phantom, Solflare, etc.) to the current account.
   * Used by profile.vue (§6.9). Caller is expected to have a Solana wallet
   * connected via solana-wallets-vue — useSupabaseAuth pulls signMessage
   * from there.
   */
  async function linkSolanaWallet(): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      await supabaseLinkSolanaWallet()
      return true
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      return false
    } finally {
      state.value.isLoading = false
    }
  }

  /**
   * Link an OAuth provider (Google) to the current account.
   * Used by profile.vue (§6.9). Triggers a redirect to the provider.
   */
  async function linkOAuth(provider: OAuthProvider): Promise<boolean> {
    state.value.isLoading = true
    state.value.error = null

    try {
      await supabaseLinkOAuth(provider)
      return true
    } catch (error: unknown) {
      state.value.error = authErrorText(error)
      state.value.isLoading = false
      return false
    }
    // No finally — successful linkOAuth redirects to provider; flag is
    // cleared on next page load.
  }

  // ─── Logout ───────────────────────────────────────────────────────

  /**
   * Full logout flow: Backend -> Supabase -> Wallet -> Clear stores
   */
  async function logout(): Promise<void> {
    state.value.isLoading = true

    try {
      // Get access token for logout
      const accessToken = await getAccessToken()

      // Logout from backend first
      if (accessToken) {
        await logoutApi(accessToken)
      }

      // Then logout from Supabase
      await supabaseLogout()

      // Disconnect EVM wallet if connected
      if (evmWallet.isConnected.value) {
        evmWallet.disconnect()
      }

      // Unsubscribe from balance updates
      unsubscribeFromBalance()
      unsubscribeFromDeposits()

      // Clear stores
      userStore.clearUser()
      if (typeof window !== 'undefined') {
        localStorage.removeItem('aurumrun_session_id')
      }
      clearTopbarCache()
      balanceStore.clearBalance()
      characterStore.reset()
      oreStore.clearOre()
      // Bonus wagering status + the session voucher latch are module/tab-scoped,
      // so without this the next user on this device inherits the previous user's
      // bonus strip and "Activated" voucher state.
      bonusStore.clear()
      resetVoucherActivationState()
      clearSentryUser()

      // Redirect to home
      await router.push('/')
    } catch (error) {
      console.error('[useAuth] Logout error:', error)
      // Always attempt Supabase logout even when backend logout fails so
      // local session storage doesn't outlive the backend session.
      try { await supabaseLogout() } catch { /* ignore — best effort */ }
      // Clear local state even if API call fails
      userStore.clearUser()
      if (typeof window !== 'undefined') {
        localStorage.removeItem('aurumrun_session_id')
      }
      clearTopbarCache()
      balanceStore.clearBalance()
      characterStore.reset()
      oreStore.clearOre()
      bonusStore.clear()
      resetVoucherActivationState()
      evmWallet.disconnect()
    } finally {
      state.value.isLoading = false
    }
  }

  // ─── Session ──────────────────────────────────────────────────────

  /**
   * Check if user has existing session
   * Called on app load to restore session
   */
  async function checkAuth(): Promise<boolean> {
    if (!supabaseAuthenticated.value) return false

    try {
      const accessToken = await getAccessToken()
      if (!accessToken) return false

      const result = await fetchMe(accessToken)

      if (result?.user) {
        userStore.setUser(result.user)
        writeTopbarCacheFromUser(result.user)
        await balanceStore.fetchBalance(accessToken)
        // Non-blocking: cosmetics and ore are visual-only, don't block auth flow
        characterStore.fetchInventory().catch(() => {})
        oreStore.fetchOreData(accessToken).catch(() => {})

        // Subscribe to real-time balance and deposit updates
        subscribeToBalance(result.user.id)
        subscribeToDeposits(result.user.id)

        return true
      }

      return false
    } catch (error) {
      console.error('[useAuth] checkAuth failed:', error)
      return false
    }
  }

  /**
   * Clear error state
   */
  function clearError(): void {
    state.value.error = null
  }

  /**
   * Open login modal
   */
  function openLoginModal(): void {
    uiStore.openLoginModal()
  }

  return {
    // State
    state,
    // Computed
    isAuthenticated: computed(() => userStore.isAuthenticated),
    isLoading: computed(() => state.value.isLoading),
    error: computed(() => state.value.error),
    walletAddress,
    solanaAddress,
    authChainType,
    // Methods — Email
    sendEmailCode,
    verifyEmailCode,
    // Methods — OAuth
    loginWithOAuth,
    completeOAuthLogin,
    // Methods — Wallet
    loginWithEvm,
    loginWithSolana,
    // Methods — Account linking
    sendLinkEmailCode,
    linkEmailWithCode,
    linkEvmWallet,
    linkSolanaWallet,
    linkOAuth,
    // Methods — Session
    logout,
    checkAuth,
    clearError,
    openLoginModal,
  }
}
